import { type EligibilityContext } from '@heidi/runtime/shell';
import { useWorkspaceId } from '@heidi/workspace/data';
import { useRef } from 'react';

import type { UserBillingInfo } from '../../../contracts/types/commerce.types';
import type { CreditAllowanceResponse } from '../../../contracts/types/credit-allowance.types';
import {
  getCreditAllowanceNudgeVariant,
  isComplimentaryCreditAllowance,
} from '../../../data/query/credit-allowance.utils';
import {
  getCreditAllowanceThresholdBillingPlanKey,
  isCreditAllowanceThresholdBillingPlanResolved,
} from '../../settings/get-credit-allowance-threshold-billing-plan-key';
import { isCreditUsageSurfaceEnabled } from '../../settings/is-credit-usage-surface-enabled';
import { isMaxBillingSeat } from '../../settings/is-max-billing-seat';
import {
  type CreditAllowanceThresholdConsumedState,
  isThresholdConsumedInRecord,
} from './creditAllowanceThresholdStorage';
import { resolveUsageNudgeCell, resolveUsageNudgeThreshold } from './resolve-usage-nudge';

export type SideNavCreditUsageThresholdPopoverLaunchGate = Pick<
  EligibilityContext,
  'isHeidi2OptInEnabled' | 'isUserManaged'
> & {
  /** `release-growth-heidi-2-usage-nudges`. */
  isUsageNudgesEnabled: boolean;
  /**
   * Active or paused consult. The tier framework is default off, so the queue
   * cannot hold this T3 card until the session ends.
   */
  isClinicalRecordingActive: boolean;
  /**
   * `useShouldRenderSidebarCreditUsagePopover`: a queued card with no tile to
   * anchor to would hold the queue slot unseen.
   */
  isSidebarTileShown: boolean;
  isTrialing: boolean;
  /** `undefined` while the team role is still loading. */
  isTeamAdmin: boolean | undefined;
  /** False while a workspace switch is hard-reloading the app. */
  isWorkspaceIdSettled: boolean;
  /** The app build check, read where `isAppleCompliantBuild()` is read today. */
  isAppleCompliantBuild: () => boolean;
};

type CreditUsageLaunchBillingInfo =
  | (NonNullable<Parameters<typeof isCreditAllowanceThresholdBillingPlanResolved>[0]> &
      Partial<Pick<UserBillingInfo, 'heidi_version'>>)
  | null
  | undefined;

/**
 * A workspace switch writes the new id and then hard-reloads (`workspace-top-menu`),
 * so the id changing mid-session means this document is on its way out — the flag
 * resync for the new workspace must not flash the popover before the reload lands.
 * `null -> id` is boot hydration, not a switch.
 */
export function useWorkspaceIdSettledForCreditUsagePopoverLaunch(): boolean {
  const workspaceId = useWorkspaceId();
  const hydratedWorkspaceIdRef = useRef<string | null>(null);

  if (workspaceId && !hydratedWorkspaceIdRef.current) {
    hydratedWorkspaceIdRef.current = workspaceId;
  }

  return !workspaceId || hydratedWorkspaceIdRef.current === workspaceId;
}

/**
 * Opens the sidebar usage card once at 90% and once at 100% of a cycle, for the
 * members the upgrade-nudge matrix gives a card to. Allowance and billing come
 * from GlobalModalsWrapper hooks, not a cache peek.
 */
export function computeShouldQueueSideNavCreditUsageThresholdPopover(
  gate: SideNavCreditUsageThresholdPopoverLaunchGate,
  billingInfo: CreditUsageLaunchBillingInfo,
  allowance: CreditAllowanceResponse | null | undefined,
  consumedThresholds: CreditAllowanceThresholdConsumedState = {}
): boolean {
  if (
    !gate.isUsageNudgesEnabled ||
    !gate.isSidebarTileShown ||
    !gate.isWorkspaceIdSettled
  ) {
    return false;
  }
  // T3 R1 and R2. The tier framework is default off, so the queue cannot
  // hold this during a consult or hide it from a managed member.
  if (
    gate.isClinicalRecordingActive ||
    gate.isUserManaged ||
    gate.isAppleCompliantBuild()
  ) {
    return false;
  }
  if (
    !isCreditUsageSurfaceEnabled({
      isHeidi2OptInEnabled: gate.isHeidi2OptInEnabled,
      heidiVersion: billingInfo?.heidi_version,
    })
  ) {
    return false;
  }
  if (!isCreditAllowanceThresholdBillingPlanResolved(billingInfo) || allowance == null) {
    return false;
  }

  const cell = resolveUsageNudgeCell({
    tier: billingInfo.new_product_tier,
    isMaxSeat: isMaxBillingSeat(billingInfo),
    isTrialing: gate.isTrialing,
    isComplimentaryAllowance: isComplimentaryCreditAllowance(allowance),
    isTeamAdmin: gate.isTeamAdmin,
  });
  if (cell == null) {
    return false;
  }

  const threshold = resolveUsageNudgeThreshold(getCreditAllowanceNudgeVariant(allowance));
  if (threshold == null) {
    return false;
  }

  const billingPlanKey = getCreditAllowanceThresholdBillingPlanKey(billingInfo);
  return !isThresholdConsumedInRecord(
    consumedThresholds,
    billingPlanKey,
    allowance.cycle_end,
    threshold
  );
}
